import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { processingDocumentsStorage } from "@/lib/processingDocumentsStorage";
import { ContentExtractionService } from "@/services/knowledgebase/ContentExtractionService";

export type UploadMutationType =
  | "audio"
  | "deep-research"
  | "document"
  | "image"
  | "notes"
  | "pdf"
  | "pdf-url"
  | "quick-research"
  | "scrape"
  | "video"
  | "website"
  | "website-brightdata"
  | "website-crawl"
  | "youtube";

interface UseUploadMutationOptions {
  type: UploadMutationType;
  onSuccess?: () => void;
}

interface MutationParams {
  [key: string]: unknown;
}

const getMutationFn = (
  type: UploadMutationType,
  userId: string,
  agentId?: string,
) => {
  const getAgentParam = () => (agentId && agentId !== "none" ? agentId : undefined);

  const mutationMap: Record<UploadMutationType, (params: MutationParams) => Promise<unknown>> = {
    youtube: async (params) => {
      return ContentExtractionService.extractYouTubeTranscript(
        params.url as string,
        userId,
        getAgentParam(),
      );
    },
    website: async (params) => {
      return ContentExtractionService.extractWebsiteContent(
        params.url as string,
        userId,
        getAgentParam(),
      );
    },
    "website-brightdata": async (params) => {
      return ContentExtractionService.extractWebsiteContentBrightData(
        params.url as string,
        userId,
        getAgentParam(),
      );
    },
    "website-crawl": async (params) => {
      return ContentExtractionService.crawlWebsiteBrightData(
        params.url as string,
        userId,
        params.maxDepth as number,
        params.maxPages as number,
        getAgentParam(),
      );
    },
    document: async (params) => {
      return ContentExtractionService.extractDocumentText(
        params.file as File,
        userId,
        getAgentParam(),
      );
    },
    audio: async (params) => {
      return ContentExtractionService.transcribeAudio(
        params.file as File,
        userId,
        "en",
        getAgentParam(),
      );
    },
    scrape: async (params) => {
      return ContentExtractionService.scrapeWebsite(
        params.url as string,
        userId,
        params.maxDepth as number,
        params.maxPages as number,
        getAgentParam(),
      );
    },
    notes: async (params) => {
      return ContentExtractionService.createNotesDocument(
        params.content as string,
        userId,
        params.title as string,
        getAgentParam(),
      );
    },
    "quick-research": async (params) => {
      return ContentExtractionService.performQuickResearch(
        params.query as string,
        userId,
        params.title as string,
        getAgentParam(),
      );
    },
    "deep-research": async (params) => {
      return ContentExtractionService.performDeepResearch(
        params.query as string,
        userId,
        params.title as string,
        getAgentParam(),
      );
    },
    "pdf-url": async (params) => {
      return ContentExtractionService.extractPdfFromUrl(
        params.url as string,
        userId,
        getAgentParam(),
      );
    },
    pdf: async (params) => {
      return ContentExtractionService.uploadPdfFile(params.file as File, userId, getAgentParam());
    },
    image: async (params) => {
      return ContentExtractionService.uploadImageFile(params.file as File, userId, getAgentParam());
    },
    video: async (params) => {
      return ContentExtractionService.uploadVideoFromUrl(
        params.fileUrl as string,
        params.fileName as string,
        userId,
        getAgentParam(),
      );
    },
  };

  return mutationMap[type];
};

const getSuccessMessage = (type: UploadMutationType): string => {
  const messageMap: Record<UploadMutationType, string> = {
    youtube: "YouTube transcript extracted successfully.",
    website: "Website content extracted successfully.",
    "website-brightdata": "Website content extracted successfully.",
    "website-crawl": "Crawl queued. Results will be available soon.",
    document: "Document text extracted successfully.",
    audio: "Audio transcribed successfully.",
    scrape: "Scraping queued. Results will be available soon.",
    notes: "Notes document created successfully.",
    "quick-research": "Quick research queued. Results will be available soon.",
    "deep-research": "Deep research queued. Results will be available soon.",
    "pdf-url": "PDF extracted successfully.",
    pdf: `PDF uploaded. Optimization in progress - check back soon.`,
    image: `Image uploaded. Text extraction in progress - check back soon.`,
    video: `Video uploaded. Transcription in progress - check back soon.`,
  };
  return messageMap[type];
};

const getToastType = (type: UploadMutationType): "success" | "info" => {
  if (["scrape", "website-crawl", "quick-research", "deep-research", "pdf", "image", "video"].includes(type)) return "info";
  return "success";
};

export const useUploadMutation = (
  type: UploadMutationType,
  agentId?: string,
  onSuccess?: () => void,
) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  if (!user?.id) {
    throw new Error("User not authenticated");
  }

  return useMutation({
    mutationFn: (params: MutationParams) => {
      const fn = getMutationFn(type, user.id, agentId);
      return fn(params);
    },
    onSuccess: (data: any) => {
      const toastType = getToastType(type);
      const message = getSuccessMessage(type);

      if (toastType === "info") {
        toast.info(message);
      } else {
        toast.success(message);
      }

      if (data?.document?.id) {
        processingDocumentsStorage.add({
          documentId: data.document.id,
          agentId,
          startedAt: Date.now(),
        });
        window.dispatchEvent(new Event("processing-documents-changed"));
      }

      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });

      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error("Failed to process upload");
      console.error("Upload error:", error);
    },
  });
};
